import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Linmere New York (18 Oct 2026): product page of the "Dressed for the city" design
 * (sections/lnm-city-product.liquid), taken from the approved prototype (Product v7) and wired to the store:
 * - color swatches swap the gallery to that color's photos; size buttons have no default and the add
 *   button asks for one ("Choose a size to add this coat.")
 * - price, availability (crossed sizes), the hidden variant id and ?variant= follow the choice
 * - add to cart posts to the cart and announces it with the standard `shopify:cart:lines-update`
 *   event, so the theme's cart drawer opens and re-renders like a normal add (and the shipping
 *   protection auto-add still runs); same pattern as assets/lnm-bundle.js
 * - phone bar after the add button scrolls away, closing band's add button, sale countdown (rolling,
 *   cookie lnm_countdown_<date>, as in the theme), size chart units, "Wear it with" recommendations.
 * - offer bars (19 Oct 2026, snippets/lnm-city-offer-picker.liquid, products with an "Offer: ..." tag): the
 *   chosen bar sets how many items the add buttons put in the cart; with 2+ items each item has its own
 *   color and size (untouched ones follow the main picker) and all go in one request. The discount is
 *   Shopify's automatic discount of the offer; prices here are an estimate made the same way.
 */

const dataEl = document.getElementById('lcProductData');
const data = dataEl ? JSON.parse(dataEl.textContent || '{}') : null;
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const lc = (window.lc = window.lc || {});

if (data) init();
initRecos();

function init() {
  const options = data.options || [];
  const variants = data.variants || [];
  const media = data.media || [];
  const colorIndex = data.colorIndex;
  const state = { values: (data.preselected || []).slice(), index: 0 };

  const $ = (id) => document.getElementById(id);
  const thumbs = $('lcThumbs');
  const strip = $('lcStrip');
  const mainImg = $('lcMainImg');
  const mainTag = $('lcMainTag');
  const colorVal = $('lcColorVal');
  const buyMsg = $('lcBuyMsg');
  const addBtn = $('lcAddBtn');
  const addBtn2 = $('lcAddBtn2');
  const addBtn3 = $('lcAddBtn3');
  const form = $('lcAddForm');
  const idInput = $('lcVariantId');
  const priceEl = $('lcPrice');
  const stickyPrice = $('lcStickyPrice');
  const buyBox = $('lcBuy');
  const finalHeading = document.querySelector('[data-lc-final-heading]');
  const buttons = [addBtn, addBtn2, addBtn3].filter(Boolean);
  const offer = $('lcOffer');
  const offerMsg = $('lcOfferMsg');
  let busy = false;

  /* ---------- offer bars ---------- */
  let fmt = null;
  try {
    fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: data.currency || 'USD' });
  } catch (err) {
    fmt = null;
  }
  const money = (cents) => (fmt ? fmt.format(cents / 100) : '$' + (cents / 100).toFixed(2));

  function tierEl() {
    return offer ? offer.querySelector('.lc-tier[data-selected]') : null;
  }

  function tierQty(el) {
    return el ? Number(el.dataset.qty) || 1 : 1;
  }

  /** Price of the bar for a unit price in cents: items paid (Buy X Get Y) or percent off each item, cents dropped. */
  function tierTotal(el, cents) {
    const qty = tierQty(el);
    if (el && el.dataset.pay) return cents * Number(el.dataset.pay);
    const pct = el ? Number(el.dataset.pct) || 0 : 0;
    return (cents - Math.floor((cents * pct) / 100)) * qty;
  }

  function clearOfferMsg() {
    if (offerMsg) offerMsg.textContent = '';
    if (offer) offer.querySelectorAll('.lc-piece-sel.is-error').forEach((sel) => sel.classList.remove('is-error'));
  }

  /** Untouched item selectors follow the main color and size picker. */
  function syncPieces() {
    if (!offer) return;
    offer.querySelectorAll('.lc-piece-sel').forEach((sel) => {
      if (sel.dataset.touched) return;
      const value = state.values[Number(sel.dataset.opt)];
      if (value === null || value === undefined) {
        if (sel.querySelector('option[value=""]')) sel.value = '';
      } else {
        sel.value = value;
      }
    });
  }

  function renderOffer(v) {
    if (!offer) return;
    const checked = offer.querySelector('.lc-tier-radio:checked');
    offer.querySelectorAll('.lc-tier').forEach((el) => {
      const on = !!checked && el.contains(checked);
      el.toggleAttribute('data-selected', on);
      const pieces = el.querySelector('.lc-pieces');
      if (pieces) pieces.hidden = !on;
      if (v && typeof v.cents === 'number') {
        const priceEl = el.querySelector('[data-price]');
        if (priceEl) priceEl.textContent = money(tierTotal(el, v.cents));
        const compareEl = el.querySelector('[data-compare]');
        if (compareEl) compareEl.textContent = money(v.cents * tierQty(el));
      }
    });
  }

  if (offer) {
    offer.addEventListener('change', (e) => {
      const t = e.target;
      if (!(t instanceof HTMLElement)) return;
      if (t.matches('.lc-tier-radio')) {
        clearOfferMsg();
        update();
      } else if (t.matches('.lc-piece-sel')) {
        t.dataset.touched = 'true';
        t.classList.remove('is-error');
        if (offerMsg) offerMsg.textContent = '';
      }
    });
  }

  function offerFail(message, select) {
    if (offerMsg) {
      offerMsg.className = 'lc-msg';
      offerMsg.textContent = message;
    }
    if (select) {
      select.classList.add('is-error');
      select.focus({ preventScroll: true });
    }
    return null;
  }

  /** Items of the chosen bar when it has 2+ items, as [{id, quantity}], or null (error shown). */
  function offerItems() {
    const el = tierEl();
    const qty = tierQty(el);
    const rows = el ? Array.from(el.querySelectorAll('.lc-piece')) : [];
    const counts = new Map();
    if (rows.length === 0) {
      /* product without choices: the same variant qty times */
      const v = resolved() || variants.find((x) => x.available) || variants[0];
      if (!v || !v.available) return offerFail('This item is sold out.');
      counts.set(v.id, qty);
    } else {
      for (const row of rows) {
        const n = row.dataset.piece;
        const wanted = state.values.slice();
        for (const sel of row.querySelectorAll('.lc-piece-sel')) {
          const oi = Number(sel.dataset.opt);
          if (!sel.value) return offerFail('Choose a ' + options[oi].name.toLowerCase() + ' for item ' + n + '.', sel);
          wanted[oi] = sel.value;
        }
        const match = variants.find((v) => v.options.every((val, i) => val === wanted[i]));
        const first = row.querySelector('.lc-piece-sel');
        if (!match) return offerFail('Item ' + n + ': that combination is not available. Please choose another.', first);
        if (!match.available) return offerFail('Item ' + n + ': ' + match.options.join(' / ') + ' is sold out. Please choose another.', first);
        counts.set(match.id, (counts.get(match.id) || 0) + 1);
      }
    }
    return Array.from(counts, ([id, quantity]) => ({ id, quantity }));
  }

  /* ---------- gallery ---------- */
  function colorPos() {
    if (colorIndex < 0) return -1;
    return options[colorIndex].values.indexOf(state.values[colorIndex]);
  }

  function photos() {
    const c = colorPos();
    const own = media.filter((m) => m.g === c && c >= 0);
    const shared = media.filter((m) => m.g === -1);
    const list = own.concat(shared);
    return list.length ? list : media;
  }

  function renderGallery(keepIndex) {
    const list = photos();
    if (!keepIndex) state.index = 0;
    if (thumbs) thumbs.textContent = '';
    if (strip) strip.textContent = '';
    list.forEach((p, i) => {
      if (thumbs) {
        const b = document.createElement('button');
        b.className = 'lc-thumb';
        b.type = 'button';
        b.setAttribute('aria-label', 'Photo ' + (i + 1) + ': ' + p.alt);
        b.setAttribute('aria-pressed', i === state.index ? 'true' : 'false');
        const im = document.createElement('img');
        im.src = p.thumb;
        im.alt = '';
        im.loading = i > 2 ? 'lazy' : 'eager';
        im.width = 88;
        im.height = 110;
        b.appendChild(im);
        b.addEventListener('click', () => show(i));
        thumbs.appendChild(b);
      }
      if (strip) {
        const f = document.createElement('figure');
        const im2 = document.createElement('img');
        im2.src = p.strip;
        im2.alt = p.alt;
        im2.loading = i > 1 ? 'lazy' : 'eager';
        im2.width = 1000;
        im2.height = 1250;
        f.appendChild(im2);
        if (p.cap) {
          const cap = document.createElement('figcaption');
          cap.textContent = p.cap;
          f.appendChild(cap);
        }
        strip.appendChild(f);
      }
    });
    if (strip) strip.scrollLeft = 0;
    show(state.index, true);
  }

  function show(i, instant) {
    const list = photos();
    const p = list[i];
    if (!p) return;
    state.index = i;
    if (thumbs) {
      Array.prototype.forEach.call(thumbs.children, (b, k) => b.setAttribute('aria-pressed', k === i ? 'true' : 'false'));
    }
    if (!mainImg) return;
    const swap = () => {
      mainImg.srcset = p.srcset;
      mainImg.src = p.src;
      mainImg.alt = p.alt;
      if (mainTag) {
        mainTag.textContent = p.cap;
        mainTag.hidden = !p.cap;
      }
      mainImg.classList.remove('is-out');
    };
    if (instant || reduceMotion) {
      swap();
      return;
    }
    mainImg.classList.add('is-out');
    setTimeout(swap, 180);
  }

  /* ---------- options ---------- */
  function matches(v, values, skip) {
    return values.every((val, i) => i === skip || val === null || v.options[i] === val);
  }

  function resolved() {
    if (state.values.some((v) => v === null)) return null;
    return variants.find((v) => matches(v, state.values, -1)) || null;
  }

  /** variant whose price is shown while a size is still missing */
  function shown() {
    return resolved() || variants.find((v) => matches(v, state.values, -1) && v.available) || variants.find((v) => matches(v, state.values, -1)) || variants[0];
  }

  function priceHtml(v, stickyOrder) {
    const was = v.compare ? '<span class="lc-was">' + v.compare + '</span>' : '';
    if (stickyOrder) return '<span class="lc-now">' + v.price + '</span>' + was;
    return v.compare
      ? '<span class="lc-sr">Regular price</span>' + was + '<span class="lc-sr">Sale price</span><span class="lc-now">' + v.price + '</span>'
      : '<span class="lc-now">' + v.price + '</span>';
  }

  function setAddText(v, added) {
    if (!addBtn) return;
    const exact = resolved();
    if (exact && !exact.available) {
      addBtn.disabled = true;
      addBtn.textContent = 'Sold out';
      return;
    }
    addBtn.disabled = false;
    const el = tierEl();
    if (added && tierQty(el) < 2) {
      addBtn.textContent = 'Add another';
      return;
    }
    addBtn.textContent = 'Add to cart ';
    const span = document.createElement('span');
    span.id = 'lcAddPrice';
    span.textContent = '· ' + (el && typeof v.cents === 'number' ? money(tierTotal(el, v.cents)) : v.price);
    addBtn.appendChild(span);
  }

  function update() {
    const v = shown();
    if (priceEl) priceEl.innerHTML = priceHtml(v, false);
    if (stickyPrice) stickyPrice.innerHTML = priceHtml(v, true);
    if (finalHeading) {
      const words = finalHeading.dataset.lcFinalHeading.replace('[price]', v.price);
      if (finalHeading.dataset.split !== 'true') finalHeading.textContent = words;
      else {
        finalHeading.setAttribute('aria-label', words);
        const priceWord = finalHeading.querySelector('.lc-motion-word');
        if (priceWord && /\d/.test(priceWord.textContent)) priceWord.textContent = v.price + ',';
      }
    }
    syncPieces();
    renderOffer(v);
    setAddText(v, false);

    const exact = resolved();
    if (exact && idInput) idInput.value = String(exact.id);
    if (exact) {
      const url = new URL(window.location.href);
      if (url.searchParams.get('variant') !== String(exact.id)) {
        url.searchParams.set('variant', String(exact.id));
        window.history.replaceState(window.history.state, '', url.toString());
      }
    }

    /* crossed values: nothing available with that value and the other choices */
    document.querySelectorAll('.lc-product .lc-opt').forEach((group) => {
      const oi = Number(group.dataset.opt);
      group.querySelectorAll('[data-value]').forEach((btn) => {
        const value = btn.dataset.value;
        const ok = variants.some((x) => x.available && x.options[oi] === value && matches(x, state.values, oi));
        btn.toggleAttribute('data-unavailable', !ok);
      });
    });
  }

  function clearMsg() {
    if (!buyMsg) return;
    buyMsg.textContent = '';
    buyMsg.className = 'lc-msg';
  }

  document.querySelectorAll('.lc-product .lc-opt').forEach((group) => {
    const oi = Number(group.dataset.opt);
    group.addEventListener('click', (e) => {
      const b = e.target.closest('[data-value]');
      if (!b || !group.contains(b)) return;
      state.values[oi] = b.dataset.value;
      group.querySelectorAll('[data-value]').forEach((s) => s.setAttribute('aria-pressed', s === b ? 'true' : 'false'));
      const choices = group.querySelector('.lc-sizes');
      if (choices) choices.classList.remove('is-error');
      if (oi === colorIndex) {
        if (colorVal) colorVal.textContent = b.dataset.value;
        renderGallery(false);
      } else {
        const val = group.querySelector('[data-val]');
        if (val) val.textContent = b.dataset.value;
      }
      clearMsg();
      clearOfferMsg();
      update();
    });
  });

  /* ---------- add to cart ---------- */
  function cartSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids);
  }

  function choiceText(v) {
    const parts = [];
    v.options.forEach((value, i) => {
      if (options[i].values.length < 2) return;
      parts.push(i === colorIndex ? value : options[i].name.toLowerCase() + ' ' + value);
    });
    return parts.join(', ');
  }

  async function addToBag(fromBottom) {
    if (busy) return;
    const bar = tierEl();
    if (bar && tierQty(bar) > 1) {
      clearOfferMsg();
      const items = offerItems();
      if (!items) {
        if (fromBottom && offer) lc.scrollTo ? lc.scrollTo(offer) : offer.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
        return;
      }
      const label = bar.querySelector('.lc-tier-text strong');
      const qty = tierQty(bar);
      await sendLines(items, 'Added: ' + (label ? label.textContent.trim() : qty + ' items') + ' (' + qty + ' items).', shown());
      return;
    }
    const missing = state.values.indexOf(null);
    if (missing >= 0) {
      const group = document.querySelector('.lc-product .lc-opt[data-opt="' + missing + '"]');
      const choices = group ? group.querySelector('.lc-sizes') : null;
      if (choices) choices.classList.add('is-error');
      if (buyMsg) {
        buyMsg.className = 'lc-msg';
        buyMsg.textContent = 'Choose a ' + options[missing].name.toLowerCase() + ' to add this ' + (data.item || 'item') + '.';
      }
      if (fromBottom && buyBox) lc.scrollTo ? lc.scrollTo(buyBox) : buyBox.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
      const first = choices ? choices.querySelector('[data-value]') : null;
      if (first) first.focus({ preventScroll: !fromBottom });
      return;
    }
    const variant = resolved();
    if (!variant || !variant.available) return;
    let text = (data.msgAdded || 'Added: [choice].').replace('[choice]', choiceText(variant));
    if (data.bundle && data.msgBundle) text += ' ' + data.msgBundle;
    await sendLines([{ id: variant.id, quantity: 1 }], text, variant);
  }

  /** Adds the lines in one request and announces it like a normal add (drawer opens and re-renders). */
  async function sendLines(items, okText, variant) {
    busy = true;
    buttons.forEach((b) => { b.disabled = true; });
    if (addBtn) addBtn.textContent = 'Adding';

    const routes = (window.Theme && window.Theme.routes) || {};
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    const source = form || document.body;
    source.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const sections = cartSectionIds();
      const response = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(','), sections_url: window.location.pathname }),
      }).then((r) => r.json());
      if (response && typeof response.status === 'number' && response.status >= 400) {
        throw new Error(response.description || response.message || 'Unable to add it. Try again.');
      }
      const cart = await fetch((routes.cart_url || '/cart') + '.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          sections: response.sections ?? undefined,
          source: 'lnm-city-product',
          productId: String(data.id),
          didError: false,
        },
      });
      const okEl = items.length && tierQty(tierEl()) > 1 && offerMsg ? offerMsg : buyMsg;
      if (okEl) {
        okEl.className = 'lc-msg ok';
        okEl.textContent = okText;
      }
      buttons.forEach((b) => { b.disabled = false; });
      setAddText(variant, true);
      const cartBtn = document.getElementById('lcCartBtn');
      if (cartBtn && window.gsap && !reduceMotion) {
        window.gsap.fromTo(cartBtn, { scale: 1 }, { scale: 1.12, duration: 0.25, yoyo: true, repeat: 1, ease: 'power2.out' });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to add it. Try again.';
      deferred.reject(error);
      source.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      const errEl = tierQty(tierEl()) > 1 && offerMsg ? offerMsg : buyMsg;
      if (errEl) {
        errEl.className = 'lc-msg';
        errEl.textContent = message;
      }
      buttons.forEach((b) => { b.disabled = false; });
      setAddText(variant, false);
    } finally {
      busy = false;
    }
  }

  if (form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      addToBag(false);
    });
  }
  if (addBtn2) addBtn2.addEventListener('click', () => addToBag(true));
  if (addBtn3) addBtn3.addEventListener('click', () => addToBag(true));

  /* phone bar: appears once the add button has scrolled past */
  const stickyBar = $('lcStickyBar');
  if (stickyBar && addBtn && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        const on = !en.isIntersecting && en.boundingClientRect.top < 0;
        stickyBar.classList.toggle('is-on', on);
        stickyBar.setAttribute('aria-hidden', on ? 'false' : 'true');
        if (addBtn3) addBtn3.tabIndex = on ? 0 : -1;
      });
    }, { threshold: 0 });
    io.observe(addBtn);
  }

  /* size chart units */
  const chart = $('lcChart');
  if (chart) {
    chart.addEventListener('click', (e) => {
      const b = e.target.closest('.lc-unit');
      if (!b) return;
      chart.classList.toggle('is-cm', b.dataset.unit === 'cm');
      chart.querySelectorAll('.lc-unit').forEach((u) => u.setAttribute('aria-pressed', u === b ? 'true' : 'false'));
    });
  }

  /* sale countdown, rolling mode as in the theme: each visitor gets a random end between data-min and
     data-max minutes the first time they see it that day (cookie lnm_countdown_<date>); at zero it starts again */
  const cd = $('lcCountdown');
  if (cd) {
    const cdMin = Number(cd.dataset.min) || 165;
    const cdMax = Math.max(cdMin, Number(cd.dataset.max) || 194);
    const hEl = cd.querySelector('[data-h]');
    const mEl = cd.querySelector('[data-m]');
    const sEl = cd.querySelector('[data-s]');
    const pad = (n) => String(n).padStart(2, '0');
    const cookieName = () => {
      const d = new Date();
      return 'lnm_countdown_' + d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
    };
    const readCookie = (name) => {
      try {
        const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return m ? Number(m[1]) : 0;
      } catch (err) {
        return 0;
      }
    };
    const writeCookie = (name, value) => {
      try {
        document.cookie = name + '=' + value + '; max-age=86400; path=/; SameSite=Lax';
      } catch (err) {}
    };
    const rollingEnd = (restart) => {
      const name = cookieName();
      const stored = readCookie(name);
      if (stored > 0 && !restart) return stored;
      const minutes = cdMin + Math.floor(Math.random() * (cdMax - cdMin + 1));
      const end = Date.now() + minutes * 60000;
      writeCookie(name, end);
      return end;
    };
    let cdEnd = rollingEnd(false);
    const tick = () => {
      let left = cdEnd - Date.now();
      if (left < 0) {
        cdEnd = rollingEnd(true);
        left = cdEnd - Date.now();
      }
      const total = Math.max(0, Math.floor(left / 1000));
      hEl.textContent = pad(Math.floor(total / 3600));
      mEl.textContent = pad(Math.floor((total % 3600) / 60));
      sEl.textContent = pad(total % 60);
      setTimeout(tick, 1000 - (Date.now() % 1000));
    };
    tick();
  }

  renderGallery(false);
  update();
}

/* "Wear it with" without products picked on the product: Shopify's recommendations */
function initRecos() {
  document.querySelectorAll('[data-lc-recos]:not([data-lc-filled])').forEach((section) => {
    const url = section.getAttribute('data-lc-recos');
    if (!url) return;
    fetch(url, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        if (!html) return;
        const docx = new DOMParser().parseFromString(html, 'text/html');
        const fresh = docx.querySelector('.lc-recos');
        const target = section.querySelector('.lc-recos');
        if (!fresh || !target || !fresh.children.length) return;
        target.innerHTML = fresh.innerHTML;
        section.hidden = false;
        section.setAttribute('data-lc-filled', '');
        if (lc.revealGroup) lc.revealGroup(section);
      })
      .catch(() => {});
  });
}

/**
 * Photos at their own size (2 Nov 2026): the gallery frames take the proportion of the first photo (--lc-ar, see
 * assets/lnm-city-pdp.css). A photo more than 10% off that proportion gets .lc-fit-whole so it shows whole instead
 * of being cut. Runs on every photo load, so it also covers the photos swapped in when the color changes.
 */
(function fitGalleryPhotos() {
  const gallery = document.querySelector('.lc-gallery');
  if (!gallery) return;
  const frame = parseFloat(getComputedStyle(gallery).getPropertyValue('--lc-ar')) || 0.8;
  const check = (img) => {
    if (!img.naturalWidth || !img.naturalHeight) return;
    const ratio = img.naturalWidth / img.naturalHeight;
    img.classList.toggle('lc-fit-whole', Math.abs(ratio - frame) / frame > 0.1);
  };
  gallery.addEventListener(
    'load',
    (event) => {
      if (event.target instanceof HTMLImageElement) check(event.target);
    },
    true
  );
  gallery.querySelectorAll('img').forEach((img) => {
    if (img.complete) check(img);
  });
})();
